import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import Spinner from 'components/Spinner/Spinner';
import { canUseDOM } from 'exenv';
import formatSortFields from 'utility/formatSortFields';
import {
  Dropdown,
  Select,
  Item,
  Menu,
  Field,
} from '@zendeskgarden/react-dropdowns';
import LinkWithParams from 'components/LinkWithParams/LinkWithParams';
import { Grid, Col, Row } from '@zendeskgarden/react-grid';
import { SM, LG, MD } from '@zendeskgarden/react-typography';
import { useBranch } from 'baobab-react/hooks';
import PageViewWrapper from 'higherOrder/PageViewWrapper';
import { HelmetWrapper } from 'utility/DefaultMeta';
import {
  forEach,
  isEmpty,
  omit,
  map,
  get,
  filter,
  startCase,
  cloneDeep,
  noop,
  replace,
  sortBy,
} from 'lodash';

import { getRfqMeta } from 'state/requestForQuote/actions';
import {
  fetchGlobalMetallargyGroupDetails,
  fetchGlobalMetallargyDetails,
} from 'state/global/actions';
import {
  getCategoriesProducts,
  onChangeCriteria,
  setActiveRfqItem,
} from 'state/categories/actions';

import BreadCrumbs from 'components/BreadCrumbs/BreadCrumbs';
import { PublicItemSelectionFilters } from 'components/ItemSelectionFilters/ItemSelectionFilters';
import PublicProductsTable from 'components/PublicProductsTable/PublicProductsTable';
import GetStarted from 'components/PublicPages/GetStarted';
import { variables } from 'theme/variables';
import ErrorPage from 'routes/Error/Error';
import {
  alloyShape,
  locationShape,
  matchShape,
  paginationShape,
} from 'propTypesObjects';
import { webRefactorSwitch } from 'utility/environment';
import CategoryFilterLinks from './components/CategoryFilterLinks/CategoryFilterLinks';
import CategoryAlloyHero from './components/CategoryAlloyHero/CategoryAlloyHero';

import {
  CategoriesWrapper,
  CategoriesWrapperInner,
  Filters,
  Content,
  CategoryItem,
  CategoriesSectionHeader,
  CategoriesSection,
  CategoriesSectionInner,
} from './CategoryDetails.styles';
import CategoryDetailsRedesign from './CategoryDetailsRedesign';

const {
  custom_light_grey: customLightGrey,
  public_app_max_width: publicAppMaxWidth,
} = variables;

function categoryQueryGenerator({ alloyId, alloyGroupId }) {
  return {
    metallurgy: alloyId ? [Number(alloyId)] : undefined,
    metallurgy_group: alloyGroupId ? [Number(alloyGroupId)] : undefined,
  };
}

function getMeta(contentDetails, location) {
  if (!contentDetails) {
    return null;
  }

  const groupName = get(contentDetails, 'alloyGroupDetails.result.name');
  const dGroupName = get(
    contentDetails,
    'alloyDetails.result.metallurgy_group_name'
  );
  const dAlloyName = get(contentDetails, 'alloyDetails.result.name');

  function formatAlloyName(
    _dAlloyName,
    _dGroupName,
    _groupName,
    _contentDetails
  ) {
    if (_dAlloyName) {
      return `${_dGroupName} ${_dAlloyName} Alloy`;
    }
    if (_groupName) {
      return `${_groupName} Alloys`;
    }
    return `Search Results: ${get(
      _contentDetails,
      'keyword',
      'PipeSearch Categories'
    )}`;
  }
  const alloyFormattedName = formatAlloyName(
    dAlloyName,
    dGroupName,
    groupName,
    contentDetails
  );

  const title = `PipeSearch - ${alloyFormattedName}`;
  const description = get(contentDetails, 'description');
  const image = get(contentDetails, 'images[0].image');

  function determineUrl() {
    if (canUseDOM) {
      return window.location.href;
    }
    if (location) {
      return location.pathname;
    }
    return '';
  }

  const properties = {
    title,
    description,
    image,
    url: determineUrl(),
  };
  const meta = [];

  forEach(properties, (v, k) => {
    if (v) {
      meta.push({ property: `og:${k}`, content: v });
      meta.push({ name: `twitter:${k}`, content: v });
    }
  });
  meta.push({ name: 'description', content: description });
  meta.push({ name: 'robots', content: 'all' });

  const json = {
    '@context': 'http://schema.org/',
    '@type': 'product',
    name: title,
    description,
  };

  const script = [
    { type: 'application/ld+json', innerHTML: JSON.stringify(json, null, 2) },
  ];

  const link = [];
  return { title, meta, link, script };
}

function CategoryDetails({ match, location }) {
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(true);
  const { catType, alloyGroupSlug, alloySlug } = match?.params || {};

  const {
    alloyList,
    categories,
    rfqItemFilters,
    rfqMetadata,
    alloyGroupDetails,
    alloyDetails,
  } = useBranch({
    categories: ['categories', 'c'],
    alloyList: ['global', 'alloyLists'],
    rfqItemFilters: ['categories', 'categoriesItemFilter', 'rfqItemFilters'],
    rfqMetadata: ['requestForQuote', 'meta', 'result'],
    alloyGroupDetails: ['global', 'alloyGroupDetails', alloyGroupSlug || ''],
    alloyDetails: ['global', 'alloyDetails', alloySlug || ''],
    similarListings: ['listings', 'similarListings', 'cat' || ''],
  });

  const keyword = location?.query?.keyword;

  const [categoryCriteria, setCategoryCriteria] = useState(
    categoryQueryGenerator({
      alloyId: alloyDetails?.result?.id,
      alloyGroupId: alloyGroupDetails?.result?.id,
    })
  );

  useEffect(() => {
    setCategoryCriteria(
      categoryQueryGenerator({
        alloyId: alloyDetails?.result?.id,
        alloyGroupId: alloyGroupDetails?.result?.id,
      })
    );
  }, [alloyDetails?.result?.id, alloyGroupDetails?.result?.id]);

  const metadata = getMeta(
    { alloyDetails, alloyGroupDetails, keyword },
    location
  );
  const hasRfqMetadata = !!rfqMetadata;

  useEffect(() => {
    if (alloyList && isEmpty(rfqMetadata)) {
      getRfqMeta();
    }
  }, [alloyList, rfqMetadata]);

  const fetchCategories = useCallback((data, _categoryCriteria) => {
    const _data = cloneDeep(data || {});
    _data.criteria = _data.criteria || {};
    _data.criteria = { ..._data.criteria, ...(_categoryCriteria || {}) };
    getCategoriesProducts(_data);
  }, []);

  const hasAlloyDataRef = useRef(null);
  hasAlloyDataRef.current = (alloySlug && alloyDetails?.result) || !alloySlug;
  const alloyGroupDetailsResultRef = useRef(null);
  alloyGroupDetailsResultRef.current = alloyGroupDetails?.result;

  useEffect(() => {
    // We use refs here because we only want to refetch if criteria change (not when results are updated)
    // DEBUG - if this still doesn't fetch the most recent criteria, it's possible initial requests are still in loading state and our request is being deferred
    // See notes in asyncTreeRequester
    // This would require us to make sure alloyGroupDetails.loading is not true when we send a request (without causing a request loop, because we don't want to send another request every time loading becomes false)
    if (hasAlloyDataRef.current && alloyGroupDetailsResultRef.current) {
      fetchCategories({}, categoryCriteria);
    }
  }, [categoryCriteria, fetchCategories]);

  useEffect(() => {
    if (alloyGroupSlug) {
      fetchGlobalMetallargyGroupDetails(alloyGroupSlug);
    }
    if (alloySlug) {
      fetchGlobalMetallargyDetails(alloySlug);
    }
  }, [alloyGroupSlug, alloySlug]);

  useEffect(() => {
    if (hasRfqMetadata) {
      let obj = categoryCriteria;
      if (categoryCriteria?.metallurgy) {
        // we only want the selected metallurgy
        obj = { metallurgy: categoryCriteria?.metallurgy };
      }
      setActiveRfqItem(obj);
    }
  }, [categoryCriteria, hasRfqMetadata]);

  const paginationAndFilterPaths = {
    paths: {
      result: ['categories', 'c', 'result'],
      pagination: ['categories', 'c', 'result'],
      filters: ['categories', 'c', 'filters'],
    },
    active: {
      filters: get(categories, 'filters', {}),
      pagination: get(categories, 'result.pagination', {}),
      sort: get(categories, 'result.sort', {}),
    },
  };

  const productsAreLoading = get(categories, 'loading');

  const groupId = get(alloyGroupDetails, 'result.id');
  const groupDescription = get(alloyGroupDetails, 'result.description');
  const alloyId = get(alloyDetails, 'result.id');
  const alloyDescription = alloyDetails?.description;

  const otherAlloys = filter(
    get(alloyList, 'alloys.results'),
    (a = {}) => a.metallurgy_group === Number(groupId)
  );

  const noSlug = !alloySlug && !alloyGroupSlug;

  const groupError = get(alloyGroupDetails, 'error');
  const alloyError = get(alloyDetails, 'error');

  if (groupError === 'Not found.' || alloyError === 'Not found.') {
    return <ErrorPage />;
  }

  function renderBreadcrumbs() {
    if (catType) {
      return (
        <BreadCrumbs
          list={[
            { title: catType },
            {
              title: alloyGroupSlug,
              link: `/c/${catType}/${alloyGroupSlug}`,
            },
            {
              title: alloySlug,
              link: `/c/${catType}/${alloyGroupSlug}/${alloySlug}`,
            },
          ]}
        />
      );
    }
    if (keyword) {
      return <BreadCrumbs list={[{ title: `Search Results: ${keyword}` }]} />;
    }
    return <BreadCrumbs list={[{ title: `Categories` }]} />;
  }

  function renderOptions() {
    if (webRefactorSwitch) {
      return (
        <CategoryDetailsRedesign
          metadata={metadata}
          alloyDetails={alloyDetails}
          alloyGroupDetails={alloyGroupDetails}
          groupDescription={groupDescription}
          alloyDescription={alloyDescription}
          catType={catType}
          alloyGroupSlug={alloyGroupSlug}
          alloySlug={alloySlug}
          keyword={keyword}
          noSlug={noSlug}
          categories={categories}
          paginationAndFilterPaths={paginationAndFilterPaths}
          fetchCategories={fetchCategories}
          onChangeCriteria={onChangeCriteria}
          rfqItemFilters={rfqItemFilters}
          showAdvancedFilters={showAdvancedFilters}
          setShowAdvancedFilters={setShowAdvancedFilters}
          location={location}
          alloyList={alloyList}
          groupId={groupId}
          otherAlloys={otherAlloys}
          alloyId={alloyId}
        />
      );
    }
    return (
      <div>
        <CategoriesWrapper
          style={{
            width: '100%',
            maxWidth: publicAppMaxWidth,
            margin: '0 auto',
          }}
        >
          <HelmetWrapper metadata={metadata} />

          <div style={{ padding: '30px 20px 0', marginBottom: '10px' }}>
            <Grid>
              <Row>
                <Col>{renderBreadcrumbs()}</Col>
              </Row>
            </Grid>
          </div>

          <CategoriesWrapperInner>
            <Filters
              style={{
                maxWidth: '230px',
                minWidth: '230px',
                padding: '0 40px 0 0',
              }}
            >
              {get(alloyList, 'alloys') ? (
                <CategoryFilterLinks location={location} alloys={alloyList} />
              ) : (
                <div />
              )}
            </Filters>

            <Content>
              {noSlug ? <RawCategories alloys={alloyList} /> : null}

              {alloyDetails || alloyGroupDetails ? (
                <CategoriesSection>
                  <CategoriesSectionInner>
                    <CategoryAlloyHero
                      alloyGroupSlug={alloyGroupSlug}
                      alloySlug={alloySlug}
                      alloyGroupDetails={alloyGroupDetails}
                      alloyDetails={alloyDetails}
                    />
                  </CategoriesSectionInner>
                </CategoriesSection>
              ) : null}

              {groupId && !alloySlug ? (
                <div style={{ marginBottom: '40px' }}>
                  {groupDescription ? (
                    <Row>
                      <Col>
                        <MD
                          as="h3"
                          style={{ maxWidth: '800px', marginBottom: '40px' }}
                          paddingTopMd
                          navy
                          light
                        >
                          {groupDescription}
                        </MD>
                      </Col>
                    </Row>
                  ) : null}
                  <SM bold uppercase paddingBottomSm>
                    Alloys
                  </SM>
                  <Row>
                    {map(otherAlloys, (alloy, i) => (
                      <Col
                        style={{ maxWidth: '100px' }}
                        alignCenter
                        key={`de-${i}`}
                      >
                        <CategoryItem active={alloy.id === Number(alloyId)}>
                          <LinkWithParams to={`/c/octg/${alloy.path}`}>
                            {alloy.name}
                          </LinkWithParams>
                        </CategoryItem>
                      </Col>
                    ))}
                  </Row>
                </div>
              ) : null}

              {!noSlug ? (
                <CategoriesSection>
                  <CategoriesSectionInner>
                    <div style={{ marginBottom: '40px' }}>
                      <Row
                        alignCenter
                        justifyBetween
                        style={{ marginBottom: '20px' }}
                      >
                        <Col size="auto" alignCenter>
                          <LG scaled navy bold>
                            Alloy Products
                          </LG>
                        </Col>
                        <Col justifyEnd alignCenter>
                          <Row>
                            <Col>
                              <SM
                                style={{
                                  whiteSpace: 'nowrap',
                                  padding: '12px 0',
                                }}
                                onClick={() =>
                                  setShowAdvancedFilters(!showAdvancedFilters)
                                }
                                link
                              >
                                {showAdvancedFilters
                                  ? 'Hide Filters'
                                  : 'Product Search'}
                              </SM>
                            </Col>
                            <Col>
                              <div style={{ minWidth: '260px' }}>
                                <RenderDropdown
                                  loading={productsAreLoading}
                                  {...{
                                    sortFields: get(
                                      categories,
                                      'result.sort_fields'
                                    ),
                                    pagination: paginationAndFilterPaths,
                                    fetchMore: fetchCategories,
                                  }}
                                />
                              </div>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                      {showAdvancedFilters ? (
                        <Row>
                          <Col size="12">
                            <PublicItemSelectionFilters
                              rowIndex={0}
                              onChangeFilters={onChangeCriteria}
                              filters={omit(rfqItemFilters, [
                                'category',
                                'quantity',
                              ])}
                            />
                          </Col>
                        </Row>
                      ) : null}
                      <PublicProductsTable
                        loading={productsAreLoading}
                        items={get(categories, 'result.docs')}
                        fetchMore={({ pagination, filters, sorting }) => {
                          fetchCategories({
                            pagination,
                            criteria: filters,
                            sorting,
                          });
                        }}
                        pagination={paginationAndFilterPaths}
                      />
                    </div>
                  </CategoriesSectionInner>
                </CategoriesSection>
              ) : null}
            </Content>
          </CategoriesWrapperInner>
        </CategoriesWrapper>

        <div style={{ padding: '50px 0', background: customLightGrey }}>
          <GetStarted />
        </div>
      </div>
    );
  }

  return !(get(alloyDetails, 'result') || get(alloyGroupDetails, 'result')) &&
    groupId ? (
    <Spinner />
  ) : (
    renderOptions()
  );
}

CategoryDetails.propTypes = {
  match: matchShape,
  location: locationShape,
};

function RenderDropdown({ loading, sortFields, pagination, fetchMore }) {
  const [localSort, setLocalSort] = useState();
  const sort =
    pagination?.active?.sort?.sort && !loading
      ? pagination?.active?.sort
      : localSort || {};

  function onClickSort({ field }) {
    const data = formatSortFields({ field, sort });
    setLocalSort(data.sorting);
    fetchMore({
      ...data,
      filters: pagination?.active?.filters,
      pagination: pagination?.active?.pagination,
    });
  }

  return (
    <Dropdown selectedItem={sort.sort} onSelect={noop}>
      <Field>
        <Select
          style={{ minWidth: '100%', maxWidth: '100%' }}
          background="#fff"
          active={false}
          fontSize="13px"
        >
          {sort.sort ? (
            <SM>
              <SM as="span" bold>
                Sorting:{' '}
              </SM>{' '}
              <SM as="span">{startCase(replace(sort?.sort, /_/g, ' '))}</SM>{' '}
              <SM as="span" grey>
                ({sort.direction})
              </SM>
            </SM>
          ) : (
            <SM slate bold>
              Sort Products
            </SM>
          )}
        </Select>
      </Field>
      <Menu maxHeight="200px">
        {map(
          sortBy(
            map(sortFields, (o) => ({ option: o })),
            'option'
          ),
          ({ option }) => (
            <Item
              onClick={() => {
                onClickSort({ field: option });
              }}
              key={option}
              value={option}
            >
              {startCase(replace(option, /_/g, ' '))}
            </Item>
          )
        )}
      </Menu>
    </Dropdown>
  );
}

RenderDropdown.propTypes = {
  loading: PropTypes.bool,
  sortFields: PropTypes.arrayOf(PropTypes.string),
  pagination: paginationShape,
  fetchMore: PropTypes.func,
};

function RawCategories({ alloys }) {
  return (
    <div>
      <CategoriesSectionHeader tag="h2" style={{ marginBottom: '30px' }} bold>
        Alloy Categories
      </CategoriesSectionHeader>
      <Row style={{ marginBottom: '40px' }}>
        {map(
          sortBy(alloys?.alloys?.results, 'metallurgy_group_name'),
          (item, i, arr) => {
            const link = `/c/octg/${item.path || item.slug}`;
            const last = arr[i - 1];
            const isDiffGroup =
              (i === 0 && item.metallurgy_group_name) ||
              (last &&
                last.metallurgy_group_name !== item.metallurgy_group_name);
            return (
              <React.Fragment key={`${i}-frag`}>
                {isDiffGroup ? (
                  <Col
                    style={{ marginTop: i === 0 ? '0px' : '25px' }}
                    key={`${i}-name`}
                    size="12"
                  >
                    <SM bold uppercase paddingBottomSm>
                      {item.metallurgy_group_name}
                    </SM>
                  </Col>
                ) : null}
                <Col
                  style={{ minWidth: '140px', maxWidth: '140px' }}
                  key={`${i}-value`}
                >
                  <CategoryItem>
                    <LinkWithParams to={link}>{item.name}</LinkWithParams>
                  </CategoryItem>
                </Col>
              </React.Fragment>
            );
          }
        )}
      </Row>
    </div>
  );
}

RawCategories.propTypes = {
  alloys: PropTypes.shape({
    alloys: PropTypes.shape({
      results: PropTypes.arrayOf(alloyShape),
    }),
  }),
};

export default PageViewWrapper(CategoryDetails, {
  pageName: 'CategoryDetails',
});
